import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Image,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { Loading } from "../../../components/Loading";
import VerificationReviewPanel from "../../../components/verification/VerificationReviewPanel";
import { getFleetDetails } from "../../../actions/inventory/fleet.action";

const operationalStatusMap = {
  in_review: "In Review",
  active: "Active",
  inactive: "Inactive",
  blacklisted: "Blacklisted",
};

const operationalStatusColorMap = {
  in_review: "gray",
  active: "green",
  inactive: "red",
  blacklisted: "black",
};

const legacyOperationalStatusMap = {
  0: "in_review",
  1: "active",
  2: "inactive",
  3: "blacklisted",
};

const normalizeOperationalStatus = (value) =>
  legacyOperationalStatusMap[value] || value || "in_review";

const administrativeStatusMap = {
  0: "Available",
  1: "On Duty",
  2: "Under Maintenance",
  3: "Out Of Service",
};

const administrativeStatusColorMap = {
  0: "blue",
  1: "green",
  2: "orange",
  3: "red",
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatLabel = (value = "") =>
  String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const deriveReviewStatus = (row) => {
  const operational = normalizeOperationalStatus(row?.operationalStatus);
  const administrative = Number(row?.administrativeStatus);
  if (operational === "active" && administrative === 1) return "approved";
  if (operational === "blacklisted" || administrative === 3) return "rejected";
  return "under_review";
};

const getDocumentsStatus = (documents) => {
  if (!documents) return "Missing";
  const required = [
    documents?.registrationCertificate?.front,
    documents?.registrationCertificate?.back,
    documents?.insurance?.document,
    documents?.puc?.document,
    documents?.permit?.document,
    documents?.tax?.document,
  ];
  return required.every((value) => typeof value === "string" && value.trim()) ? "Complete" : "Incomplete";
};

const renderField = (label, value) => (
  <Box>
    <Text fontSize="xs" textTransform="uppercase" color="gray.500" mb={1}>
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="semibold" color="gray.800" wordBreak="break-word">
      {value || "—"}
    </Text>
  </Box>
);

const renderDocumentPreview = (label, imageUrl) => (
  <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={3}>
    <Text fontSize="xs" textTransform="uppercase" color="gray.500">
      {label}
    </Text>
    {imageUrl ? (
      <>
        <Box mt={2} borderRadius="md" overflow="hidden" bg="gray.50">
          <Image src={imageUrl} alt={label} w="100%" h="170px" objectFit="cover" />
        </Box>
        <Button
          as="a"
          href={imageUrl}
          target="_blank"
          rel="noreferrer"
          mt={3}
          size="sm"
          variant="outline"
        >
          Open Full Image
        </Button>
      </>
    ) : (
      <Text mt={2} fontSize="sm" color="gray.500">
        Not uploaded
      </Text>
    )}
  </Box>
);

const VehicleApprovalDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const selectedRow = location?.state?.row || null;
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchVehicleDetails = async () => {
      try {
        const response = await getFleetDetails(id);
        if (!mounted) return;
        setVehicleDetails(response?.data?.data || response?.data || null);
      } catch (error) {
        if (!mounted) return;
        toast({
          title: "Failed to fetch vehicle details",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (id && !selectedRow?.reviewOnly) {
      fetchVehicleDetails();
    } else {
      setLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [id, selectedRow?.reviewOnly, toast]);

  const resolvedData = useMemo(() => {
    const details = vehicleDetails || {};
    const documents = details?.documents || selectedRow?.documents || {};
    const operationalStatus = normalizeOperationalStatus(
      details?.operationalStatus ??
        details?.status?.operationalStatus ??
        selectedRow?.operationalStatus ??
        "in_review",
    );
    const administrativeStatus = Number(
      details?.administrativeStatus ??
        details?.status?.administrativeStatus ??
        selectedRow?.administrativeStatus ??
        0,
    );

    return {
      vehicleNumber: details?.vehicleNumber || selectedRow?.vehicleNumber || "—",
      vehicleType:
        details?.vehicleType ||
        details?.vehicle?.segmentName ||
        selectedRow?.vehicleType ||
        "—",
      model: details?.model || selectedRow?.model || "—",
      manufacturer: details?.manufacturer || selectedRow?.manufacturer || "—",
      year: details?.year || "—",
      regEndDate: details?.regEndDate || details?.registrationExpiryDate || null,
      city:
        details?.vehicleLocationCity ||
        details?.vehicleLocation?.city ||
        details?.vehicleLocation?.district ||
        "—",
      state: details?.vehicleLocationState || details?.vehicleLocation?.state || "—",
      ownerType: details?.ownerType || selectedRow?.ownerType || "—",
      ownerId: details?.ownerId || selectedRow?.ownerId || "—",
      submittedAt: selectedRow?.submittedAt || details?.createdAt || null,
      operationalStatus,
      administrativeStatus,
      reviewStatus:
        selectedRow?.reviewStatus ||
        deriveReviewStatus({
          operationalStatus,
          administrativeStatus,
        }),
      documentsStatus:
        selectedRow?.documentsStatus || getDocumentsStatus(documents),
      backendOperationalStatus:
        details?.operationalStatus ?? details?.status?.operationalStatus,
      unsaved: Boolean(selectedRow?.unsaved),
      registrationFront:
        details?.registrationFront || documents?.registrationCertificate?.front || "",
      registrationBack:
        details?.registrationBack || documents?.registrationCertificate?.back || "",
      insuranceImage: details?.insuranceImage || documents?.insurance?.document || "",
      fitnessCertificate:
        details?.fitnessCertificate || documents?.fitness?.document || "",
      pucCertificate: details?.pucCertificate || documents?.puc?.document || "",
      permitCertificate:
        details?.permitCertificate || documents?.permit?.document || "",
      taxDocument: details?.taxDocument || documents?.tax?.document || "",
      vehicleImage: details?.vehicleImage || documents?.vehicleImage?.document || "",
      ownershipProof:
        details?.ownershipProof || documents?.ownershipProof?.document || "",
    };
  }, [selectedRow, vehicleDetails]);

  if (loading) return <Loading />;

  if (!selectedRow && !vehicleDetails) {
    return (
      <Stack p={6} gap={4}>
        <BreadCrumbMaker
          items={[
            { link: "/verification", title: "Verification Center" },
            { link: "/verification/vehicle-approvals", title: "Vehicle Approvals" },
            { link: "#", title: "Vehicle Details", on: true },
          ]}
        />
        <Box bg="white" borderRadius="xl" p={6} shadow="sm">
          <Text color="gray.600">Vehicle details not found.</Text>
          <Button mt={4} onClick={() => navigate("/verification/vehicle-approvals")}>
            Back to Vehicle Approvals
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack p={6} gap={4}>
      <BreadCrumbMaker
        items={[
          { link: "/verification", title: "Verification Center" },
          { link: "/verification/vehicle-approvals", title: "Vehicle Approvals" },
          { link: "#", title: "Vehicle Details", on: true },
        ]}
      />

      <VerificationReviewPanel
        entityType="vehicle"
        entityId={selectedRow?.reviewOnly ? selectedRow?.entityId || null : selectedRow?.entityId || id}
      />

      <Box bg="white" borderRadius="xl" p={6} shadow="sm">
        <Flex justify="space-between" align="center" mb={5} gap={3} wrap="wrap">
          <Text fontSize="xl" fontWeight="bold" color="gray.700">
            Vehicle Details
          </Text>
          {/* <Button variant="outline" onClick={() => navigate("/verification/vehicle-approvals")}>
            Back to List
          </Button> */}
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {renderField("Vehicle Number", resolvedData.vehicleNumber)}
          {renderField("Vehicle Type", resolvedData.vehicleType)}
          {renderField("Model", resolvedData.model)}
          {renderField("Manufacturer", resolvedData.manufacturer)}
          {renderField("Year", resolvedData.year)}
          {renderField("Reg End Date", formatDate(resolvedData.regEndDate))}
          {renderField("City", resolvedData.city)}
          {renderField("State", resolvedData.state)}
          {renderField("Owner Type", formatLabel(resolvedData.ownerType))}
          {renderField("Owner ID", resolvedData.ownerId)}
        </SimpleGrid>

        <Divider my={5} />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {renderField("Submitted At", formatDate(resolvedData.submittedAt))}
          {renderField("Review Status", formatLabel(resolvedData.reviewStatus))}
          {renderField("Documents", resolvedData.documentsStatus)}
          {renderField(
            "Current Backend Status",
            operationalStatusMap[normalizeOperationalStatus(resolvedData.backendOperationalStatus)] || "—",
          )}
          <Box>
            <Text fontSize="xs" textTransform="uppercase" color="gray.500" mb={1}>
              Operational Status
            </Text>
            <Badge colorScheme={operationalStatusColorMap[resolvedData.operationalStatus] || "gray"}>
              {operationalStatusMap[resolvedData.operationalStatus] || "—"}
            </Badge>
          </Box>
          <Box>
            <Text fontSize="xs" textTransform="uppercase" color="gray.500" mb={1}>
              Administrative Status
            </Text>
            <Badge
              colorScheme={administrativeStatusColorMap[resolvedData.administrativeStatus] || "gray"}
            >
              {administrativeStatusMap[resolvedData.administrativeStatus] || "—"}
            </Badge>
          </Box>
          {resolvedData.unsaved ? (
            <Box>
              <Text fontSize="xs" textTransform="uppercase" color="gray.500" mb={1}>
                Persistence
              </Text>
              <Tooltip
                label="This row has local frontend-only changes. Refreshing the list page will reset these changes."
                hasArrow
                placement="top-start"
              >
                <Badge colorScheme="purple">Local only - Unsaved</Badge>
              </Tooltip>
            </Box>
          ) : null}
        </SimpleGrid>

        <Divider my={5} />
        <Text fontSize="sm" fontWeight="semibold" mb={3}>
          Uploaded Documents
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {renderDocumentPreview("Registration Front", resolvedData.registrationFront)}
          {renderDocumentPreview("Registration Back", resolvedData.registrationBack)}
          {renderDocumentPreview("Insurance", resolvedData.insuranceImage)}
          {renderDocumentPreview("Fitness Certificate", resolvedData.fitnessCertificate)}
          {renderDocumentPreview("PUC Certificate", resolvedData.pucCertificate)}
          {renderDocumentPreview("Permit Certificate", resolvedData.permitCertificate)}
          {renderDocumentPreview("Tax Document", resolvedData.taxDocument)}
          {renderDocumentPreview("Vehicle Image", resolvedData.vehicleImage)}
          {renderDocumentPreview("Ownership Proof", resolvedData.ownershipProof)}
        </SimpleGrid>
      </Box>
    </Stack>
  );
};

export default VehicleApprovalDetails;
